import { Routes } from '@angular/router';
import { authGuard } from './core/auth/auth.guard';
import { featureAccessGuard } from './core/feature-access/feature-access.guard';
import { superAdminGuard } from './core/feature-access/super-admin.guard';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () =>
      import('./pages/login/login-page.component').then(m => m.LoginPageComponent),
  },
  {
    path: '',
    loadComponent: () =>
      import('./shared/components/shell.component').then(m => m.ShellComponent),
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/dashboard/dashboard-page.component')
            .then(m => m.DashboardPageComponent),
      },
      {
        path: 'retailers',
        loadComponent: () =>
          import('./pages/retailers/retailers-overview-page.component')
            .then(m => m.RetailersOverviewPageComponent),
      },
      {
        path: 'retailers/:id',
        loadComponent: () =>
          import('./pages/retailers/retailer-detail-page.component')
            .then(m => m.RetailerDetailPageComponent),
      },
      {
        path: 'collection-points',
        loadComponent: () =>
          import('./pages/collection-points/collection-points-list-page.component')
            .then(m => m.CollectionPointsListPageComponent),
      },
      {
        path: 'collection-points/:id',
        loadComponent: () =>
          import('./pages/collection-points/collection-point-detail-page.component')
            .then(m => m.CollectionPointDetailPageComponent),
      },
      {
        path: 'collection-points/:id/status',
        loadComponent: () =>
          import('./pages/collection-points/collection-point-status-page.component')
            .then(m => m.CollectionPointStatusPageComponent),
      },
      {
        path: 'collection-points/:id/history',
        loadComponent: () =>
          import('./pages/collection-points/collection-point-history-page.component')
            .then(m => m.CollectionPointHistoryPageComponent),
      },
      {
        path: 'issues',
        loadComponent: () =>
          import('./pages/issues/issues-list-page.component')
            .then(m => m.IssuesListPageComponent),
      },
      {
        path: 'issues/:id',
        loadComponent: () =>
          import('./pages/issues/issue-detail-page.component')
            .then(m => m.IssueDetailPageComponent),
      },
      {
        path: 'admin/bulk-resolve-jobs',
        loadComponent: () =>
          import('./pages/admin/bulk-resolve-jobs-page.component')
            .then(m => m.BulkResolveJobsPageComponent),
      },
      {
        path: 'admin/feature-access',
        canActivate: [superAdminGuard],
        loadComponent: () =>
          import('./pages/admin/feature-access-page.component')
            .then(m => m.FeatureAccessPageComponent),
      },
      {
        path: 'admin/bulk-resolve-jobs/:jobId',
        loadComponent: () =>
          import('./pages/admin/bulk-resolve-job-detail-page.component')
            .then(m => m.BulkResolveJobDetailPageComponent),
      },
      {
        path: 'admin/message-sources',
        loadComponent: () =>
          import('./pages/admin/message-sources-page.component')
            .then(m => m.MessageSourcesPageComponent),
      },
      {
        path: 'support-tickets',
        loadComponent: () =>
          import('./pages/support-tickets/support-tickets-list-page.component')
            .then(m => m.SupportTicketsListPageComponent),
      },
      {
        path: 'support-tickets/:id',
        loadComponent: () =>
          import('./pages/support-tickets/support-ticket-detail-page.component')
            .then(m => m.SupportTicketDetailPageComponent),
      },
      {
        path: 'anomalies',
        loadComponent: () =>
          import('./pages/anomalies/anomaly-cohorts-page.component')
            .then(m => m.AnomalyCohortsPageComponent),
      },
      { path: 'operations/carrier-activity', redirectTo: 'transportation/carrier-activity', pathMatch: 'full' },
      {
        path: 'messages',
        loadComponent: () =>
          import('./pages/messages/messages-list-page.component')
            .then(m => m.MessagesListPageComponent),
      },
      {
        path: 'messages/:id',
        loadComponent: () =>
          import('./pages/messages/message-detail-page.component')
            .then(m => m.MessageDetailPageComponent),
      },
      {
        path: 'invoicing',
        loadComponent: () =>
          import('./pages/invoicing/invoicing-candidates-list-page.component')
            .then(m => m.InvoicingCandidatesListPageComponent),
      },
      {
        path: 'invoicing/import-progress',
        loadComponent: () =>
          import('./pages/invoicing/import-progress-page.component')
            .then(m => m.ImportProgressPageComponent),
      },
      {
        path: 'invoicing/dispatch',
        loadComponent: () =>
          import('./pages/invoicing/dispatch-page.component')
            .then(m => m.DispatchPageComponent),
      },
      {
        path: 'invoicing/origin-proposals',
        loadComponent: () =>
          import('./pages/invoicing/origin-proposals-page.component')
            .then(m => m.OriginProposalsPageComponent),
      },
      {
        path: 'reconciliation',
        loadComponent: () =>
          import('./pages/reconciliation/reconciliation-page.component')
            .then(m => m.ReconciliationPageComponent),
      },
      {
        path: 'container-types',
        loadComponent: () =>
          import('./pages/container-types/container-types-list-page.component')
            .then(m => m.ContainerTypesListPageComponent),
      },
      {
        path: 'shipping-locations',
        loadComponent: () =>
          import('./pages/shipping-locations/shipping-locations-list-page.component')
            .then(m => m.ShippingLocationsListPageComponent),
      },
      {
        path: 'shipping-locations/type-rules',
        loadComponent: () =>
          import('./pages/shipping-locations/type-rules/shipping-location-type-rules-page.component')
            .then(m => m.ShippingLocationTypeRulesPageComponent),
      },
      {
        path: 'reporting/pickup-requests',
        loadComponent: () =>
          import('./pages/reporting/operator-pickup-requests-list-page.component')
            .then(m => m.OperatorPickupRequestsListPageComponent),
      },
      {
        path: 'reporting/pickup-requests/:orderId',
        loadComponent: () =>
          import('./pages/reporting/operator-pickup-request-workbench-page.component')
            .then(m => m.OperatorPickupRequestWorkbenchPageComponent),
      },
      {
        path: 'transportation/pickup-requests',
        loadComponent: () =>
          import('./pages/transportation/pickup-requests-list-page.component')
            .then(m => m.PickupRequestsListPageComponent),
      },
      {
        path: 'transportation/pickup-requests/:orderId',
        loadComponent: () =>
          import('./pages/transportation/pickup-request-detail-page.component')
            .then(m => m.PickupRequestDetailPageComponent),
      },
      {
        path: 'transportation/carrier-activity',
        loadComponent: () =>
          import('./pages/transportation/carrier-activity-page.component')
            .then(m => m.CarrierActivityPageComponent),
      },
      {
        path: 'transportation/carrier-activity/:reportId',
        loadComponent: () =>
          import('./pages/transportation/carrier-activity-detail-page.component')
            .then(m => m.CarrierActivityDetailPageComponent),
      },
      {
        path: 'transportation/container-journeys',
        loadComponent: () =>
          import('./pages/transportation/container-journey-page.component')
            .then(m => m.ContainerJourneyPageComponent),
      },
      {
        path: 'transportation/pending-scan-queues',
        loadComponent: () =>
          import('./pages/transportation/pickup-queue-overview-page.component')
            .then(m => m.PickupQueueOverviewPageComponent),
      },
      {
        path: 'transportation/routes',
        canActivate: [featureAccessGuard('transportation.routes')],
        loadComponent: () =>
          import('./pages/transportation/routes-page.component')
            .then(m => m.RoutesPageComponent),
      },
      {
        path: 'transportation/trigger-reconciliation',
        loadComponent: () =>
          import('./pages/transportation/trigger-reconciliation-page.component')
            .then(m => m.TriggerReconciliationPageComponent),
      },
      {
        path: 'warehouse/receipts',
        loadComponent: () =>
          import('./pages/warehouse/goods-receipts-list-page.component')
            .then(m => m.GoodsReceiptsListPageComponent),
      },
      {
        path: 'warehouse/receipts/:scanId',
        loadComponent: () =>
          import('./pages/warehouse/goods-receipt-detail-page.component')
            .then(m => m.GoodsReceiptDetailPageComponent),
      },
      {
        path: 'warehouse/import-progress',
        loadComponent: () =>
          import('./pages/warehouse/import-progress-page.component')
            .then(m => m.ImportProgressPageComponent),
      },
      {
        path: 'reporting/non-conformities',
        loadComponent: () =>
          import('./pages/non-conformities/non-conformities-list-page.component')
            .then(m => m.NonConformitiesListPageComponent),
      },
      {
        path: 'reporting/non-conformities/:bagId',
        loadComponent: () =>
          import('./pages/non-conformities/non-conformity-detail-page.component')
            .then(m => m.NonConformityDetailPageComponent),
      },
      {
        path: 'diagnostics/backend-errors',
        loadComponent: () =>
          import('./pages/diagnostics/backend-errors-list-page.component')
            .then(m => m.BackendErrorsListPageComponent),
      },
      {
        path: 'diagnostics/backend-errors/:id',
        loadComponent: () =>
          import('./pages/diagnostics/backend-error-detail-page.component')
            .then(m => m.BackendErrorDetailPageComponent),
      },
      {
        path: 'diagnostics/system',
        loadComponent: () =>
          import('./pages/diagnostics/system-resources-page.component')
            .then(m => m.SystemResourcesPageComponent),
      },
      {
        path: 'diagnostics/projections',
        loadComponent: () =>
          import('./pages/diagnostics/projections-health-page.component')
            .then(m => m.ProjectionsHealthPageComponent),
      },
      {
        path: 'diagnostics/data-actions',
        loadComponent: () =>
          import('./pages/diagnostics/data-actions-page.component')
            .then(m => m.DataActionsPageComponent),
      },
      {
        path: 'dev/admin-data-table-demo',
        loadComponent: () =>
          import('./pages/dev/admin-data-table-demo-page.component')
            .then(m => m.AdminDataTableDemoPageComponent),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
